import { Extension, getHTMLFromFragment } from '@tiptap/react';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Fragment, type Node as PMNode } from '@tiptap/pm/model';
import { Transform } from '@tiptap/pm/transform';

import { getEditorHTML, isEditorAlive } from '@/utils/editor-utils';

import { byteLength, changedRange, escapeBytes, sizeDelta, type SizeEstimate, textByteLength } from './size-estimate';

// Firestore rejects documents over 1 MiB (1_048_576 bytes); the gap leaves room for the
// document name (capped at MAX_DOCUMENT_NAME_LENGTH), the other fields and Firestore's
// per-document overhead.
export const CONTENT_SIZE_BUDGET = 1_000_000;

// Longest name, in UTF-16 code units, a note, Blueprint or block may be saved with.
export const MAX_DOCUMENT_NAME_LENGTH = 320;

// Room kept for formatting once text fills a document: a formatting change adds tens of bytes of
// markup, so the reserve covers hundreds of them.
export const FORMATTING_RESERVE = 10_000;

// What edits that add text may grow a document to. Edits that add no text may use the reserve.
export const TEXT_BUDGET = CONTENT_SIZE_BUDGET - FORMATTING_RESERVE;

// What to tell the user when an edit or an agent run is refused for its size.
export const CONTENT_SIZE_LIMIT_NOTIFICATION = {
  type: 'warning',
  title: 'You’ve reached the text limit!',
  description: 'We don’t support content that’s over 1 MB yet.',
  toastOptions: { id: 'content-size-limit' },
} as const;

// UTF-8 bytes a saved document stores for `doc`: its HTML (`content`) plus its plain-text copy
// (`parsedContent`). Pass `html` when it is already serialized, such as an editor's memoized HTML.
export const documentByteLength = (doc: PMNode, html = getHTMLFromFragment(doc.content, doc.type.schema)) =>
  byteLength(html) + textByteLength(doc, 0, doc.content.size);

const ANCHOR_REFRESH_DELAY_MS = 1000;

// Longest an opened editor waits for the browser to be idle before it is measured.
const IDLE_REFRESH_TIMEOUT_MS = 5000;

// Exact measurements a paste trim may spend before it gives up and rejects the paste.
const MAX_TRIM_MEASUREMENTS = 3;

export interface ContentSizeLimitOptions {
  onLimitReached: () => void;
}

// A document's size and how far it may be from the exact size: 0 after an exact measurement,
// growing with each estimated edit since.
interface SizeRecord {
  size: number;
  drift: number;
}

// Docs are immutable and a doc's size depends only on the doc, so a size recorded for a doc
// stays valid for it, in any editor.
const sizeByDoc = new WeakMap<PMNode, SizeRecord>();

// Measures a document with `documentByteLength`.
export const ContentSizeLimit = Extension.create<ContentSizeLimitOptions>({
  name: 'contentSizeLimit',

  addOptions() {
    return {
      onLimitReached: () => {},
    };
  },

  addProseMirrorPlugins() {
    const { editor, options } = this;

    const measure = (doc: PMNode): SizeRecord => {
      const measured = { size: exactSize(doc), drift: 0 };
      sizeByDoc.set(doc, measured);
      return measured;
    };

    // `getEditorHTML` memoizes per doc and is shared with autosave.
    const exactSize = (doc: PMNode) =>
      doc === editor.state.doc ? documentByteLength(doc, getEditorHTML(editor)) : documentByteLength(doc);

    const record = (doc: PMNode, previous: SizeRecord, estimate: SizeEstimate) => {
      const next = { size: previous.size + estimate.delta, drift: previous.drift + estimate.drift };
      sizeByDoc.set(doc, next);
      return next;
    };

    const fitsOnMeasure = (doc: PMNode, base: SizeRecord, limit: number) => {
      const { size } = measure(doc);
      return size <= limit || size <= base.size;
    };

    return [
      new Plugin({
        key: new PluginKey('contentSizeLimit'),

        filterTransaction: (tr, state) => {
          // Loads, remote syncs and agent writes are never blocked. Their result is measured
          // exactly by the next anchor refresh or the next user edit.
          if (!tr.docChanged || isSetContent(tr) || !editor.isEditable || isAiToolkitWrite(tr)) return true;

          // Follow-ups other plugins append to an accepted edit are not user input. ProseMirror
          // filters them against the state that edit produced, before it marks them
          // `appendedTransaction`, so that state is what tells them apart.
          if (state !== editor.state) {
            const previous = sizeByDoc.get(state.doc);
            const estimate = previous && sizeDelta(tr.before, tr.doc);
            if (estimate) record(tr.doc, previous, estimate);
            return true;
          }

          const base = sizeByDoc.get(state.doc) ?? measure(state.doc);
          const estimate = sizeDelta(tr.before, tr.doc);
          if (estimate) {
            const next = record(tr.doc, base, estimate);
            const limit = sizeLimit(addsText(estimate));
            // Shrinking is always allowed, even when the document is already over the budget.
            if (next.size + next.drift <= limit || next.size <= base.size) return true;

            // Even at its smallest, this estimate is over the limit and larger than the base.
            const surelyGrowsPastLimit = next.size - next.drift > Math.max(limit, base.size + base.drift);
            if (!surelyGrowsPastLimit && fitsOnMeasure(tr.doc, base, limit)) return true;
          } else {
            // Frames that do not line up are rare, so the text change is measured exactly too.
            const textGrows =
              textByteLength(tr.doc, 0, tr.doc.content.size) > textByteLength(state.doc, 0, state.doc.content.size);
            if (fitsOnMeasure(tr.doc, base, sizeLimit(textGrows))) return true;
          }

          options.onLimitReached();
          // A drop also deletes the dragged content at its source, so only pastes are trimmed.
          if (!isPaste(tr)) return false;
          const exactBase = base.drift === 0 ? base : measure(state.doc);
          const trimmedSize = trimPaste(tr, exactBase.size, exactSize);
          if (trimmedSize === undefined) return false;
          sizeByDoc.set(tr.doc, { size: trimmedSize, drift: 0 });
          return true;
        },

        view: () => {
          let cancelAnchorRefresh = () => {};

          const refreshAnchor = () => {
            if (!isEditorAlive(editor) || !editor.isEditable || sizeByDoc.get(editor.state.doc)?.drift === 0) return;
            measure(editor.state.doc);
          };

          // An editor that opens is measured once the browser is idle; an edited one once edits
          // have paused.
          const scheduleAnchorRefresh = (whenIdle: boolean) => {
            cancelAnchorRefresh();
            if (whenIdle && typeof requestIdleCallback === 'function') {
              const handle = requestIdleCallback(refreshAnchor, { timeout: IDLE_REFRESH_TIMEOUT_MS });
              cancelAnchorRefresh = () => cancelIdleCallback(handle);
            } else {
              const timer = setTimeout(refreshAnchor, ANCHOR_REFRESH_DELAY_MS);
              cancelAnchorRefresh = () => clearTimeout(timer);
            }
          };

          scheduleAnchorRefresh(true);

          return {
            update: (view, prevState) => {
              if (view.state.doc !== prevState.doc) scheduleAnchorRefresh(false);
            },
            destroy: () => cancelAnchorRefresh(),
          };
        },
      }),
    ];
  },
});

// `setContent` is the only command that sets `preventUpdate`, and it always sets it (to
// `!emitUpdate`), so the meta's presence marks loads and remote syncs.
const isSetContent = (tr: Transaction) => typeof tr.getMeta('preventUpdate') === 'boolean';

const isAiToolkitWrite = (tr: Transaction) => !!tr.getMeta('aiToolkitStreamUpdate') || !!tr.getMeta('aiToolkitUpdate');

const isPaste = (tr: Transaction) => !!tr.getMeta('paste') || tr.getMeta('uiEvent') === 'paste';

// An edit counts as adding text when its plain text may grow within the estimate's drift, so an
// added block separator the estimate misses still counts.
const addsText = (estimate: SizeEstimate) => estimate.textDelta + estimate.drift > 0;

const sizeLimit = (addingText: boolean) => (addingText ? TEXT_BUDGET : CONTENT_SIZE_BUDGET);

// Keeps the longest prefix of the pasted content that fits under the text budget and returns the trimmed document's
// exact size, or undefined when nothing fits. The prefix comes from one walk that adds up what
// the pasted nodes and characters add to the document, which can only overcount (shared mark
// wrappers, merged blocks), so it usually needs a single exact measurement to confirm. When that
// measurement still overshoots, from structure the walk does not model, the room shrinks by the
// overshoot and the walk repeats, up to MAX_TRIM_MEASUREMENTS times.
const trimPaste = (tr: Transaction, base: number, exactSize: (doc: PMNode) => number) => {
  const range = changedRange(tr.before, tr.doc);
  if (!range) return undefined;

  const cut = (position: number) => new Transform(tr.doc).delete(position, range.endNew).doc;
  const withoutPaste = cut(range.start);
  const estimate = sizeDelta(tr.before, withoutPaste);
  const sizeWithoutPaste = estimate ? base + estimate.delta + estimate.drift : exactSize(withoutPaste);

  let room = TEXT_BUDGET - sizeWithoutPaste;
  for (let attempt = 0; attempt < MAX_TRIM_MEASUREMENTS; attempt++) {
    const end = fittingEnd(tr.doc, range.start, range.endNew, room);
    if (end <= range.start) return undefined;
    const size = exactSize(cut(end));
    if (size <= TEXT_BUDGET) {
      tr.delete(end, range.endNew);
      return size;
    }
    room -= size - TEXT_BUDGET;
  }
  return undefined;
};

// The last position between `from` and `to` up to which the content from `from` adds at most
// `room` bytes. Stops only in inline content, so a cut there closes the blocks around it. Each
// node entered after `from` adds its own markup with no content, each text node its mark
// wrappers, and each character its bytes in the HTML and the plain text.
const fittingEnd = (doc: PMNode, from: number, to: number, room: number) => {
  const shellSizes = new Map<string, number>();
  let used = 0;
  let end = from;
  let full = false;

  doc.nodesBetween(from, to, (node, position) => {
    if (full) return false;
    if (node.isText) {
      used += cachedShellSize(node, shellSizes);
      const text = node.text ?? '';
      const textEnd = Math.min(to - position, text.length);
      const fit = fittingTextEnd(text, Math.max(from - position, 0), textEnd, room - used);
      used += fit.bytes;
      end = position + fit.end;
      full = fit.end < textEnd;
      return false;
    }
    if (position < from) return true;
    used += cachedShellSize(node, shellSizes);
    if (used > room) {
      full = true;
      return false;
    }
    if (node.inlineContent) end = position + 1;
    else if (node.isInline && node.isLeaf) end = position + node.nodeSize;
    return true;
  });
  return end;
};

// What a node adds to a document with its content left out: its markup and any block separator
// and leaf text for a node, the mark wrappers for a text node. Nodes in a paste repeat their
// markup, so sizes are cached per markup.
const cachedShellSize = (node: PMNode, cache: Map<string, number>) => {
  const shell = node.isText ? node.type.schema.text('x', node.marks) : node.copy();
  const key = JSON.stringify(shell.toJSON());
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const leafText = node.isLeaf && !node.isText ? node.type.spec.leafText?.(node) ?? '' : '';
  const separator = node.isTextblock || (node.isBlock && node.isLeaf && leafText) ? 1 : 0;
  const html = byteLength(getHTMLFromFragment(Fragment.from(shell), node.type.schema));
  const size = node.isText ? html - 1 : html + byteLength(leafText) + separator;
  cache.set(key, size);
  return size;
};

// The offset up to which the characters of `text` from `from` add at most `room` bytes, and those
// bytes. A surrogate pair is kept or dropped whole.
const fittingTextEnd = (text: string, from: number, to: number, room: number) => {
  let bytes = 0;
  let index = from;
  while (index < to) {
    const code = text.charCodeAt(index);
    const pair = isHighSurrogate(code) && isLowSurrogate(text.charCodeAt(index + 1));
    const length = pair ? 2 : 1;
    const cost = 2 * utf8Length(code, pair) + escapeBytes(code);
    if (index + length > to || bytes + cost > room) break;
    bytes += cost;
    index += length;
  }
  return { end: index, bytes };
};

const isHighSurrogate = (code: number) => code >= 0xd800 && code <= 0xdbff;

const isLowSurrogate = (code: number) => code >= 0xdc00 && code <= 0xdfff;

// A lone surrogate encodes as U+FFFD, three bytes.
const utf8Length = (code: number, pair: boolean) => {
  if (pair) return 4;
  if (code < 0x80) return 1;
  if (code < 0x800) return 2;
  return 3;
};
